/**
 * On-screen diagnostic for push-notification setup.
 *
 * Visible at /debug/push.  Runs every step of the FCM token-fetch dance
 * and renders the result of each — no DevTools required.  When something
 * fails the user can screenshot this and paste it back to a developer.
 */
import React from "react";
import { Bell, Check, X, Loader2, RefreshCw } from "lucide-react";
import { initializeApp, getApps, getApp } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { api } from "@/shared/lib/api";

const cfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export default function DebugPush() {
  const [steps, setSteps] = React.useState([]);
  const [running, setRunning] = React.useState(false);

  const log = (label, status, detail) => setSteps((s) => [...s, { label, status, detail }]);

  const runDiagnostics = async () => {
    setSteps([]);
    setRunning(true);
    try {
      log("Bundle config", VAPID ? "ok" : "fail",
          VAPID ? `apiKey=${cfg.apiKey?.slice(0, 10)}…  vapid=${VAPID.slice(0, 16)}…` : "REACT_APP_FIREBASE_VAPID_KEY is undefined — bundle didn't include env vars; restart the frontend");
      if (!VAPID) return;

      log("Browser APIs", "ok", `Notification=${"Notification" in window} · ServiceWorker=${"serviceWorker" in navigator} · Push=${"PushManager" in window}`);
      if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
        log("Push support", "fail", "This browser/context does not support web push");
        return;
      }

      let supported = false;
      try { supported = await isSupported(); } catch (e) { /* ignore */ }
      log("firebase/messaging supported", supported ? "ok" : "fail", supported ? "yes" : "Not supported in this browser/context");
      if (!supported) return;

      log("Notification.permission", Notification.permission === "granted" ? "ok" : (Notification.permission === "denied" ? "fail" : "info"), Notification.permission);
      if (Notification.permission === "denied") {
        log("Permission", "fail", "Blocked at OS/browser level — re-enable in browser settings or system notifications.");
        return;
      }
      if (Notification.permission === "default") {
        const r = await Notification.requestPermission();
        log("Permission prompt", r === "granted" ? "ok" : "fail", r);
        if (r !== "granted") return;
      }

      // Initialize Firebase + Messaging
      let app;
      try {
        app = getApps().length ? getApp() : initializeApp(cfg);
        log("initializeApp", "ok", `name=${app.name}`);
      } catch (e) {
        log("initializeApp", "fail", e?.message || String(e));
        return;
      }
      let messaging;
      try {
        messaging = getMessaging(app);
        log("getMessaging", "ok", "instance ready");
      } catch (e) {
        log("getMessaging", "fail", e?.message || String(e));
        return;
      }

      // Service worker
      let reg = null;
      try {
        reg = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
        if (!reg) {
          reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
            scope: "/firebase-cloud-messaging-push-scope",
          });
          log("SW register", "ok", "newly registered");
        } else {
          log("SW register", "ok", `existing scope=${reg.scope}`);
        }
        if (reg.installing || reg.waiting) {
          log("SW activating", "info", "waiting for service worker activation…");
          await new Promise((resolve) => {
            const w = reg.installing || reg.waiting;
            if (!w) return resolve();
            w.addEventListener("statechange", () => { if (w.state === "activated") resolve(); });
            setTimeout(resolve, 5000);
          });
        }
        log("SW state", reg.active ? "ok" : "fail", `active=${!!reg.active} · installing=${!!reg.installing} · waiting=${!!reg.waiting}`);
      } catch (e) {
        log("SW register", "fail", e?.message || String(e));
        return;
      }

      // getToken
      let token = null;
      try {
        token = await getToken(messaging, { vapidKey: VAPID, serviceWorkerRegistration: reg });
        if (token) {
          log("getToken", "ok", `token tail=…${token.slice(-16)}  length=${token.length}`);
        } else {
          log("getToken", "fail", "Firebase returned an empty token (permission may have just changed).");
          return;
        }
      } catch (e) {
        log("getToken", "fail", `${e?.code || ""} ${e?.message || String(e)}`);
        return;
      }

      // Register with backend
      try {
        const { data } = await api.post("/notifications/register-token", {
          token,
          platform: detectPlatform(),
        });
        log("Backend register-token", "ok", `id=${data?.id} · rotated=${data?.rotated}`);
      } catch (e) {
        log("Backend register-token", "fail", e?.response?.data?.detail || e?.message || String(e));
        return;
      }

      // Test send
      try {
        const { data } = await api.post("/notifications/test-send", {
          title: "Link · Test Push",
          body: "If you see this on your device, push delivery is live.",
          url: "/account",
        });
        log("Test push", data?.delivered > 0 ? "ok" : "fail",
            data?.delivered > 0 ? `delivered to ${data.delivered} device(s) — check your notification center now`
                                : "Backend accepted the request but delivered=0; token may have been rejected by Firebase");
      } catch (e) {
        log("Test push", "fail", e?.response?.data?.detail || e?.message || String(e));
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#F9F7F4] text-[#0A1128] p-5 pb-32" data-testid="debug-push">
      <header className="mb-5">
        <h1 className="font-serif text-3xl">Push Diagnostics</h1>
        <p className="text-xs text-[#515E7B] mt-1">Step-by-step trace of the push-notification setup. Tap Run, then screenshot the result.</p>
      </header>
      <button
        onClick={runDiagnostics}
        disabled={running}
        className="h-12 px-5 inline-flex items-center gap-2 bg-[#0A1128] text-[#F9F7F4] rounded-full font-semibold text-[12px] tracking-[0.18em] uppercase disabled:opacity-50"
        data-testid="debug-push-run"
      >
        {running ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
        {running ? "Running…" : "Run diagnostics"}
      </button>

      <div className="mt-5 space-y-1.5">
        {steps.map((s, i) => (
          <div
            key={i}
            className="bg-white border border-black/10 px-3 py-2.5 rounded-lg flex items-start gap-3"
            data-testid={`debug-push-step-${i}`}
          >
            <span className="shrink-0 mt-0.5">
              {s.status === "ok" ? <Check size={16} className="text-emerald-600" /> :
               s.status === "fail" ? <X size={16} className="text-rose-600" /> :
               <RefreshCw size={16} className="text-[#5c97f7]" />}
            </span>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold leading-tight">{s.label}</div>
              {s.detail && <div className="text-[11px] text-[#515E7B] mt-0.5 break-all">{s.detail}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "web";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "web";
}
